<template>
  <modal-fullscreen class="shift-modal" title="" :visible="visible" :destroy-on-close="true" width="1200px"
    data-testid="shift-view-modal" @cancel="closeShiftModal">
    <modal-navigation v-if="shiftsList.length && currentIndex !== -1" :current="currentIndex" :disabled="isShiftBusy"
      :total="shiftsList.length" data-testid="shift-view-navigation" @navigate="navigate" />

    <template slot="title">
      <div class="flex align-items-center gap-2" v-if="isBaseShiftLoading || shift" data-testid="shift-view-title">
        <h2 class="m-0">
          {{ isBaseShiftLoading ? 'Shift' : modalTitle }}
        </h2>
        <template v-if="!isBaseShiftLoading">
          <a-tag v-if="shift.adjusted" class="m-0" color="#f59e0b">Adjusted</a-tag>
          <a-tag v-if="shift.manually_created" class="m-0" color="#3b82f6">Manually Created</a-tag>
          <a-tag v-if="shift.retro_shift" class="m-0" color="#6b7280">Retro Shift</a-tag>
          <a-tag v-if="isDeleted" class="m-0" color="#ef4444">Deleted</a-tag>
        </template>
        <a-tooltip>
          <template slot="title"> Copy Shift Link </template>
          <span @click="copyLink" class="action-button" data-testid="profile-view-copy-link-button"><custom-icon
              width="20" height="20" icon="carbon:link" /></span>
        </a-tooltip>
      </div>
    </template>

    <div v-if="isBaseShiftLoading || !shift" class="loading-spin" style="height: 650px"
      data-testid="shift-view-loading">
      <a-spin />
    </div>
    <div v-else data-testid="shift-view-content">
      <shift-deletion :shift-id="shift.id" :visible.sync="visibleDeletionModal" :version="shift.version"
        data-testid="shift-view-delete-modal" @edit="afterDelete" />

      <shift-edit :shift-id="shift.id" :visible.sync="visibleEditModal" data-testid="shift-view-edit-modal"
        @edit="afterEdit" />

      <shift-sign :info="{ shift_id, open_tab: 'info' }" :visible.sync="visibleSignModal"
        data-testid="shift-view-sign-modal" @edit="afterEdit" />

      <pay-code-edit v-if="hasPayCode" :shift-id="shift.id" :visible.sync="visibleEditPayCodeModal"
        data-testid="shift-view-edit-pay-code-modal" @edit="afterEdit" />

      <a-tabs v-model="activeKey" size="small" tabPosition="left" data-testid="shift-view-tabs">
        <a-tab-pane key="info">
          <span slot="tab">
            <icon type="info-circle" />
            Info
          </span>
          <info-view @delete-shift="openDeletionModal" />
        </a-tab-pane>
        <a-tab-pane key="payment" :disabled="isDeleted">
          <span slot="tab">
            <icon type="dollar" />
            Payment
          </span>
          <shift-section-state :state="sectionStates.payment" empty-title="No payment requests"
            empty-description="Payment requests that include this shift will appear here."
            error-title="Payment details couldn't be loaded" error-description="Try loading the payment details again."
            show-content-when-empty test-id="shift-view-payment-state" @retry="loadShiftPayments({ shift_id })">
            <payment-view />
          </shift-section-state>
        </a-tab-pane>
        <a-tab-pane v-if="hasFeature('authorization')" :disabled="isDeleted" key="authorizations">
          <span slot="tab">
            <icon type="exception" />
            Violations
          </span>
          <shift-section-state :state="sectionStates.authorizations" empty-title="No violations"
            empty-description="Violations for shift exceptions will appear here."
            error-title="Violations couldn't be loaded" error-description="Try loading the violations again."
            test-id="shift-view-authorizations-state" @retry="loadShiftAuthorizations({ shift_id })">
            <authorization-view :focused-authorization-id="focusedAuthorizationId" @update="afterEdit" />
          </shift-section-state>
        </a-tab-pane>
        <a-tab-pane :disabled="hasPayCode || isDeleted" key="paycode"><span slot="tab">
            <icon type="link" />
            Pay Codes
          </span>
          <shift-section-state :state="sectionStates.paycode" empty-title="No pay codes"
            empty-description="Pay codes applied to this shift will appear here."
            error-title="Pay codes couldn't be loaded" error-description="Try loading the pay codes again."
            test-id="shift-view-pay-codes-state" @retry="loadShiftPayCodes({ shift_id })">
            <pay-codes-view />
          </shift-section-state>
        </a-tab-pane>
        <a-tab-pane key="time">
          <span slot="tab">
            <icon type="clock-circle" />
            Time
          </span>
          <time-view @open-authorization="openAuthorization" />
        </a-tab-pane>
        <a-tab-pane key="confirmation" v-if="visibleCDPH">
          <span slot="tab">
            <icon type="folder-open" />
            CDPH 530
          </span>
          <confirmation-view @update="afterEdit" />
        </a-tab-pane>
        <a-tab-pane v-if="shiftTimeOffId || sectionStates.timeOff === 'error'" key="time-off">
          <span slot="tab">
            <icon type="calendar" />
            Time Off
          </span>
          <shift-section-state :state="sectionStates.timeOff" empty-title="No time off"
            empty-description="Time off linked to this shift will appear here."
            error-title="Time off couldn't be loaded" error-description="Try loading the time off again."
            test-id="shift-view-time-off-state" @retry="fetchShiftTimeOff">
            <time-off-view embedded :visible="activeKey === 'time-off'" :timeOffId.sync="shiftTimeOffId"
              :timeOffList="shiftTimeOffList" @update="afterTimeOffUpdate" />
          </shift-section-state>
        </a-tab-pane>
        <a-tab-pane v-if="hasFeature('work_area')" :disabled="hasPayCode || isDeleted" key="action"><span slot="tab">
            <icon type="history" />
            Actions
            <a-badge status="error" v-if="
              !!shift.authorizations_with_status.find(
                item =>
                  item.status === 'initial' &&
                  ['outside_work_area_authorization', 'associate_not_recognized_authorization'].includes(item.key)
              )
            " />
          </span>
          <shift-section-state :state="sectionStates.action" empty-title="No shift actions"
            empty-description="Tracked start, break, and end actions will appear here."
            error-title="Shift actions couldn't be loaded" error-description="Try loading the shift actions again."
            test-id="shift-view-actions-state" @retry="loadShiftActions({ shift_id })">
            <action-view @open-authorization="openAuthorization" />
          </shift-section-state>
        </a-tab-pane>
        <a-tab-pane key="history">
          <span slot="tab">
            <icon type="database" />
            Changes History
          </span>

          <history-view />

        </a-tab-pane>
      </a-tabs>
    </div>

    <template slot="footer">
      <div class="flex justify-content-between" v-if="isBaseShiftLoading">
        <a-button data-testid="shift-view-close-button" @click="closeShiftModal">Close</a-button>
      </div>
      <div class="flex justify-content-between" v-else-if="isDeleted || isBlockedActions">
        <a-button data-testid="shift-view-close-button" @click="closeShiftModal">Close</a-button>
      </div>
      <div class="flex justify-content-between" v-else>
        <a-button data-testid="shift-view-close-button" @click="closeShiftModal">Close</a-button>
        <div class="flex justify-content-end">
          <template v-if="shift">
            <a-button v-if="shift && (shift?.internal_payment_info?.not_ready_to_pay_flags ?? []).includes('notSigned')"
              icon="form" type="primary" :disabled="!shift || isShiftBusy" ghost data-testid="shift-view-sign-button"
              @click="openSignModal">Sign</a-button><a-button v-if="shift && hasPayCode" icon="form" type="primary"
              :disabled="!shift || isShiftBusy" ghost data-testid="shift-view-edit-pay-code-button"
              @click="openEditModal">Edit Pay Code</a-button>
            <a-button v-if="shift && !hasPayCode" icon="form" type="primary" :disabled="disabledAdjust || isShiftBusy"
              @click="openEditModal" ghost data-testid="shift-view-adjust-button">Adjust</a-button></template>
        </div>
      </div>
    </template>
  </modal-fullscreen>
</template>

<script>
import { Badge, Button, Icon, message, notification, Spin, Tabs, Tag, Tooltip } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import ShiftViewPayCode from '../info/shift-view-pay-code.vue'
import ShiftViewPayment from '../info/shift-view-payment.vue'
import ShiftViewAction from '../info/shift-view-action.vue'
import ShiftViewAuthorization from '../info/shift-view-authorization.vue'
import ShiftViewInfo from '../info/shift-view-info.vue'
import ShiftViewTime from '../info/shift-view-time.vue'
import ShiftDeletionModal from './shift-delete-modal.vue'
import ShiftSignModal from './shift-sign-modal.vue'
import ShiftPayCodeEditModal from '../pay-code/shift-pay-code-edit-modal.vue'
import shiftFormVue from '../form/shift-form.vue'
import ShiftViewConfirmation from '../info/shift-view-conformation.vue'
import permissionMixin from '@/mixins/permissionMixin'
import featureMixin from '@/mixins/featureMixin'
import ShiftViewChangesHistory from '../info/shift-view-changes-history.vue'
import TimeOffView from '@/modules/time-off/components/view/time-off-view.vue'
import ModalNavigation from '@/components/entities/modal/modal-navigation.vue'
import { buildProjectDeepLinkQuery } from '@/utils/deep-link'
import ShiftViewSectionState from '../shift-view-section-state.vue'
import AppIcon from '@/components/ui/app-icon.vue'
import moment from 'moment-timezone'
import util from '@/util.js'

const {
  mapActions: shiftActions,
  mapState: shiftState,
  mapMutations: shiftMutations
} = createNamespacedHelpers('shifts')
const { mapActions: timeOffActions } = createNamespacedHelpers('timeOff')

export default {
  components: {
    'a-button': Button,
    'a-tabs': Tabs,
    'a-tab-pane': Tabs.TabPane,
    'a-spin': Spin,
    'a-tag': Tag,
    'a-badge': Badge,
    icon: Icon,
    'a-tooltip': Tooltip,
    'custom-icon': AppIcon,
    'pay-codes-view': ShiftViewPayCode,
    'payment-view': ShiftViewPayment,
    'action-view': ShiftViewAction,
    'authorization-view': ShiftViewAuthorization,
    'confirmation-view': ShiftViewConfirmation,
    'info-view': ShiftViewInfo,
    'time-view': ShiftViewTime,
    'history-view': ShiftViewChangesHistory,
    'time-off-view': TimeOffView,
    'shift-deletion': ShiftDeletionModal,
    'shift-sign': ShiftSignModal,
    'pay-code-edit': ShiftPayCodeEditModal,
    'shift-edit': shiftFormVue,
    'modal-navigation': ModalNavigation,
    'shift-section-state': ShiftViewSectionState
  },
  mixins: [permissionMixin, featureMixin],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    shift_id: {
      type: Number,
      default: undefined
    },
    open_tab: {
      type: String,
      default: 'info'
    },
    shiftsList: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      sectionStates: {
        paycode: 'idle',
        payment: 'idle',
        action: 'idle',
        authorizations: 'idle',
        timeOff: 'idle'
      },
      loadingShift: false,
      loadingQueuedShift: false,
      fetchShiftPromise: null,

      visibleDeletionModal: false,
      visibleEditModal: false,
      visibleSignModal: false,
      visibleEditPayCodeModal: false,

      activeKey: 'info',
      currentIndex: -1,
      shiftTimeOffId: undefined,
      focusedAuthorizationId: undefined
    }
  },
  watch: {
    visible: {
      handler(newVal) {
        if (newVal) {
          this.activeKey = this.normalizeOpenTab(this.open_tab)
          this.currentIndex = this.shiftsList.findIndex(item => item === this.shift_id)

          if (this.shift_id) this.fetchShift()
        }
      },
      immediate: true
    }
  },
  computed: {
    ...shiftState({
      shift: state => state.shift.shift,
      associate: state => state.shift.associate,
      payCodes: state => state.shiftPayCodes,
      payments: state => state.shiftPayments,
      actions: state => state.shiftActions,
      authorizations: state => state.shiftAuthorizations,
    }),

    visibleCDPH() {
      if (this.shift.external_payment_info) {
        return (
          this.shift.external_payment_info.payment_type === 'Contractor' &&
          ['CNA', 'RN', 'LVN', 'Nursing Supervisor'].includes(this.shift.external_payment_info.associate.role.name)
        )
      }

      return (
        this.shift.internal_payment_info.payment_type === 'Contractor' &&
        ['CNA', 'RN', 'LVN', 'Nursing Supervisor'].includes(this.shift.internal_payment_info.associate.role.name)
      )
    },

    disabledAdjust() {
      const disabledPaymentStatuses = ['not_paid', 'cancelled', 'draft', 'partially_paid', 'paid']

      const disabledStatuses = ['missed', 'completed', 'started']

      return (
        !disabledPaymentStatuses.includes(this.shift.internal_payment_info.payment_status) ||
        !disabledStatuses.includes(this.shift.status)
      )
    },

    modalTitle() {
      return this.shift
        ? `${this.associate?.first_name ?? ''} ${this.associate?.last_name ?? ''}\`s ${this.formattedDate}`
        : 'Shift'
    },

    formattedDate() {
      const start = moment(this.shift.scheduled_start).utc(true)
      const end = moment(this.shift.scheduled_end).utc(true)

      let result = util.convertDateTimeToCurrentTimeZoneWithFormat(
        this.shift.scheduled_start,
        this.$store.state.applicationState.dateFormat
      )

      if (!start.isSame(end, 'day')) {
        result += ` - ${util.convertDateTimeToCurrentTimeZoneWithFormat(this.shift.scheduled_end, this.$store.state.applicationState.dateFormat)}`
      }

      return result
    },

    hasPayCode() {
      return this.shift.pay_code !== null
    },

    shiftTimeOffList() {
      return this.shiftTimeOffId ? [this.shiftTimeOffId] : []
    },

    isDeleted() {
      return this.shift ? this.shift.status === 'deleted' : true
    },

    isSectionLoading() {
      return Object.values(this.sectionStates).includes('loading')
    },

    isBaseShiftLoading() {
      return this.loadingShift || this.loadingQueuedShift
    },

    isShiftBusy() {
      return this.isBaseShiftLoading || this.isSectionLoading
    },

    isBlockedActions() {
      return (
        this.$store.state.applicationState.currentProject.staffing &&
        this.shift.internal_payment_info.project.id !== this.$store.state.applicationState.currentProject.id
      )
    }
  },
  methods: {
    ...shiftActions([
      'getAllShiftInfo',
      'getShiftPayCodes',
      'getShiftPayments',
      'getShiftActions',
      'fetchShiftAuthorizations',
    ]),
    ...timeOffActions(['getTimeOffByShift']),
    ...shiftMutations(['clearShiftData', 'resetShiftViewSections']),

    openDeletionModal() {
      this.visibleDeletionModal = true
    },

    normalizeOpenTab(tab) {
      if (tab === 'calculation') return 'payment'
      if (tab === 'note') return 'info'

      const supported = [
        'info',
        'payment',
        'authorizations',
        'paycode',
        'time',
        'confirmation',
        'time-off',
        'action',
        'history'
      ]

      return supported.includes(tab) ? tab : 'info'
    },

    openAuthorization(authorizationId) {
      this.focusedAuthorizationId = authorizationId
      this.activeKey = 'authorizations'
    },

    openEditModal() {
      if (this.hasPayCode) {
        this.visibleEditPayCodeModal = true
      } else {
        this.visibleEditModal = true
      }
    },

    async afterEdit(withAdjust) {
      await this.fetchShift()

      this.$emit('edit', this.shift)

      if (withAdjust) {
        this.openEditModal()
      }
    },

    async afterDelete() {
      await this.fetchShift()

      this.$emit('edit', this.shift)
      this.closeShiftModal()
    },

    async afterTimeOffUpdate() {
      await this.fetchShiftTimeOff()
      this.$emit('edit', this.shift)
    },

    openSignModal() {
      this.visibleSignModal = true
    },

    closeShiftModal() {
      this.clearShiftData()
      this.resetShiftViewSections()
      this.resetSectionStates()
      this.shiftTimeOffId = undefined
      this.focusedAuthorizationId = undefined
      this.$emit('update:visible', false)
    },

    resetSectionStates() {
      Object.keys(this.sectionStates).forEach(section => {
        this.sectionStates[section] = 'idle'
      })
    },

    async navigate(index) {
      if (this.isShiftBusy) return

      this.currentIndex = index

      this.$emit('update:shift_id', this.shiftsList[index])
      this.$emit('update:open_tab', this.normalizeOpenTab(this.activeKey))

      this.$nextTick(async () => {
        await this.fetchShift()
      })
    },

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    copyLink() {
      const mergedQuery = buildProjectDeepLinkQuery({
        currentQuery: this.$route.query,
        deepLinkQuery: {
          openShift: this.shift.id
        },
        projectId: this.$store.state.applicationState.currentProject?.id
      })

      const resolved = this.$router.resolve({
        name: this.$route.name,
        query: mergedQuery
      })

      const fullUrl = window.location.origin + resolved.href

      navigator.clipboard
        .writeText(fullUrl)
        .then(() => {
          message.success('Link to shift successfully copied!', 5)
        })
        .catch(() => {
          message.error('Unable copy link!', 5)
        })
    },

    async fetchShift() {
      const requestedShiftId = this.shift_id
      const previousRequest = this.fetchShiftPromise ?? Promise.resolve()
      const hasQueuedRequest = Boolean(this.fetchShiftPromise)
      const currentRequest = previousRequest.catch(() => { }).then(() => this.performFetchShift(requestedShiftId))

      if (hasQueuedRequest) {
        this.loadingQueuedShift = true
      }

      this.fetchShiftPromise = currentRequest

      try {
        await currentRequest
      } finally {
        if (this.fetchShiftPromise === currentRequest) {
          this.fetchShiftPromise = null
          this.loadingQueuedShift = false
        }
      }
    },

    async performFetchShift(requestedShiftId) {
      try {
        const payload = {
          shift_id: requestedShiftId
        }

        this.shiftTimeOffId = undefined
        this.focusedAuthorizationId = undefined
        this.resetShiftViewSections()
        this.resetSectionStates()

        const independentRequests = [this.fetchShiftTimeOff(requestedShiftId)]

        try {
          this.loadingShift = true
          await this.getAllShiftInfo(payload)
        } catch (error) {
          this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
        } finally {
          this.loadingShift = false
        }

        if (this.isDeleted) {
          await Promise.all(independentRequests)
          return
        }

        independentRequests.push(
          this.loadShiftPayCodes(payload),
          this.loadShiftPayments(payload),
          this.loadShiftActions(payload),
          this.loadShiftAuthorizations(payload)
        )

        await Promise.all(independentRequests)
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
        this.closeShiftModal()
      }
    },

    async loadShiftPayCodes(payload) {
      await this.loadSection(
        'paycode',
        () => this.getShiftPayCodes(payload),
        () => this.payCodes.length === 0,
        payload.shift_id
      )
    },

    async loadShiftPayments(payload) {
      await this.loadSection(
        'payment',
        () => this.getShiftPayments(payload),
        () => this.payments.length === 0,
        payload.shift_id
      )
    },

    async loadShiftActions(payload) {
      await this.loadSection(
        'action',
        () => this.getShiftActions(payload),
        () => this.actions.length === 0,
        payload.shift_id
      )
    },

    async loadShiftAuthorizations(payload) {
      await this.loadSection(
        'authorizations',
        () => this.fetchShiftAuthorizations(payload),
        () => this.authorizations.length === 0,
        payload.shift_id
      )
    },

    async loadSection(section, request, isEmpty, requestedShiftId) {
      this.sectionStates[section] = 'loading'

      try {
        await request()

        if (this.shift_id === requestedShiftId) {
          this.sectionStates[section] = isEmpty() ? 'empty' : 'ready'
        }
      } catch (error) {
        if (this.shift_id === requestedShiftId) {
          this.sectionStates[section] = 'error'
        }
      }
    },

    async fetchShiftTimeOff(requestedShiftId = this.shift_id) {
      if (!requestedShiftId) {
        this.shiftTimeOffId = undefined
        this.sectionStates.timeOff = 'empty'
        return
      }

      try {
        this.sectionStates.timeOff = 'loading'

        const timeOffId = await this.getTimeOffByShift({
          id: requestedShiftId
        })

        if (this.shift_id === requestedShiftId) {
          this.shiftTimeOffId = timeOffId
          this.sectionStates.timeOff = timeOffId ? 'ready' : 'empty'

          if (!timeOffId && this.activeKey === 'time-off') {
            this.activeKey = 'info'
          }
        }
      } catch (error) {
        if (this.shift_id === requestedShiftId) {
          this.sectionStates.timeOff = 'error'
        }
      }
    }
  }
}
</script>

<style scoped>
.shift-modal ::v-deep .ant-modal-body {
  padding-left: 0;
}

.shift-modal ::v-deep .ant-tabs-tab {
  text-align: left !important;
}

.ant-tag {
  height: fit-content;
}
</style>
